// Which filter triggers fold into More filters. Search narrows to its minimum first; then inactive triggers
// fold, then active ones, the last configured first in each group, so the visible triggers keep their
// configured order. The trigger whose panel is open never folds.

import { useEffect, useLayoutEffect, useState } from 'react'

import {
  TOOLBAR_GAP,
  pixels,
  toolbarFreeWidth,
  widthOf,
} from '../layout/Toolbar'

export const MORE_KEY = '__more'

const NONE = []

/**
 * @param {object} input
 * @param {string[]} input.keys filter keys, configured order
 * @param {Object<string, number>} input.widths trigger widths by key
 * @param {string[]} [input.active] keys whose filter is active
 * @param {?string} [input.keep] a key that never folds
 * @param {number} input.available pixels the filter group may take
 * @param {number} [input.gap] pixels between triggers
 * @param {number} [input.moreWidth] the More filters trigger's width
 * @returns {string[]} the folded keys, configured order
 */
export const foldFilters = ({
  keys,
  widths,
  active = NONE,
  keep = null,
  available,
  gap = TOOLBAR_GAP,
  moreWidth = 0,
}) => {
  if (!(available >= 0) || available === Infinity) return NONE
  const width = key => widths[key] || 0
  const shown = keys.slice()
  const folded = new Set()
  const fits = () => {
    let total = shown.reduce((sum, key) => sum + width(key), 0)
    let count = shown.length
    if (folded.size) {
      total += moreWidth
      count += 1
    }
    return total + gap * Math.max(0, count - 1) <= available
  }
  if (fits()) return NONE
  const lastFirst = list => list.slice().reverse()
  const order = lastFirst(keys.filter(key => !active.includes(key)))
    .concat(lastFirst(keys.filter(key => active.includes(key))))
    .filter(key => key !== keep)
  for (const key of order) {
    shown.splice(shown.indexOf(key), 1)
    folded.add(key)
    if (fits()) break
  }
  return keys.filter(key => folded.has(key))
}

// Infinity while the toolbar has not been laid out, so nothing folds yet.
const filterSpace = group =>
  toolbarFreeWidth(group && group.closest('.me-table-toolbar'), Infinity)

const sameKeys = (a, b) =>
  a.length === b.length && a.every((key, i) => key === b[i])

/**
 * Measures after every render and folds what does not fit. Widths come from the group's measuring copies
 * (`[data-measure]` per key, `[data-measure-more]`), which render whether or not their trigger is folded, so
 * folding never changes what is measured.
 * @param {object} input
 * @param {{current: ?HTMLElement}} input.groupRef
 * @param {{current: ?HTMLElement}} input.measureRef
 * @param {string[]} input.keys
 * @param {string[]} input.active
 * @param {?string} input.keep
 * @param {boolean} input.enabled false below the narrow breakpoint, where triggers wrap instead
 * @param {number} [input.freeWidth] the toolbar's published free width, used when positive
 * @returns {string[]}
 */
export const useFilterOverflow = ({
  groupRef,
  measureRef,
  keys,
  active,
  keep,
  enabled,
  freeWidth = 0,
}) => {
  const [folded, setFolded] = useState(NONE)
  const [, setTick] = useState(0)

  useLayoutEffect(() => {
    const group = groupRef.current
    const measure = measureRef.current
    let next = NONE
    if (enabled && group && measure) {
      const widths = {}
      let moreWidth = 0
      Array.from(measure.children).forEach(copy => {
        if (copy.dataset.measure) widths[copy.dataset.measure] = widthOf(copy)
        else if ('measureMore' in copy.dataset) moreWidth = widthOf(copy)
      })
      next = foldFilters({
        keys,
        widths,
        active,
        keep,
        available: freeWidth > 0 ? freeWidth : filterSpace(group),
        gap: pixels(window.getComputedStyle(group).columnGap, TOOLBAR_GAP),
        moreWidth,
      })
    }
    if (!sameKeys(next, folded)) setFolded(next)
  })

  // the toolbar changes width with the window and with its other controls
  useEffect(() => {
    if (!enabled) return undefined
    const remeasure = () => setTick(tick => tick + 1)
    const group = groupRef.current
    const toolbar = group && group.closest('.me-table-toolbar')
    let observer = null
    if (toolbar && typeof window.ResizeObserver === 'function') {
      observer = new window.ResizeObserver(remeasure)
      observer.observe(toolbar)
      Array.from(toolbar.children).forEach(child => {
        if (child !== group) observer.observe(child)
      })
    }
    window.addEventListener('resize', remeasure)
    return () => {
      if (observer) observer.disconnect()
      window.removeEventListener('resize', remeasure)
    }
  }, [enabled, groupRef])

  return folded
}
